import React, { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import buildUrl from '../../../buildUrl';
import { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import LoadingOverlay from '../../../loadingOverlay/LoadingOverlay';
import useSnackbar from '../../../hooks/useSnackbar';
import useApi from '../../../hooks/useApi';

const rowsPerPageOptions = [10, 25, 50];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14
    }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover
    },

    '&:last-child td, &:last-child th': {
        border: 0
    }
}));

const EmptyTable = ({ isLoading, filtersApplied }) => {
    if (isLoading) {
        return null;
    }

    return (
        <Box sx={{ padding: 6, textAlign: 'center' }}>
            {filtersApplied ? (
                <Typography>No data</Typography>
            ) : (
                <>
                    <Typography>No Nps Comments.</Typography>
                </>
            )}
        </Box>
    );
};

const RunningScores = ({ selectedYear, selectedQuarter, searchValue }) => {
    const { showSnackbar } = useSnackbar();
    const { handleGet } = useApi();
    const [isLoading, setIsLoading] = useState(true);
    const [runningScores, setRunningScores] = useState([]);
    const [runningScoresCount, setRunningScoresCount] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
    const [periods, setPeriods] = useState([]);

    const fetchRunningScoresCount = async () => {
        setIsLoading(true);

        try {
            const params = {
                search: searchValue
            };

            if (selectedYear.length > 0) {
                params.year = selectedYear;
            }

            if (selectedQuarter.length > 0) {
                params.quarter = selectedQuarter;
            }

            const url = buildUrl('superadmin/accounts/nps-running-scores/count', params);

            const response = await handleGet(url);
            const data = await response.json();

            setRunningScoresCount(data);
        }
        catch (error) {
            showSnackbar('Error fetching NPS Comments count', 'error');
        }

        setIsLoading(false);
    };

    const fetchRunningScores = async () => {
        setIsLoading(true);

        try {
            const params = {
                search: searchValue,
                skip: page * rowsPerPage,
                take: rowsPerPage
            };

            if (selectedYear.length > 0) {
                params.year = selectedYear;
            }

            if (selectedQuarter.length > 0) {
                params.quarter = selectedQuarter;
            }

            const url = buildUrl('superadmin/accounts/nps-running-scores', params);

            const response = await handleGet(url);
            const data = await response.json();

            const dataPeriods = data.length > 0 ? Object.keys(data[0].scores) : [];
            setPeriods(dataPeriods);
            setRunningScores(data);
        }
        catch (error) {
            showSnackbar('Error fetching NPS Comments count', 'error');
        }

        setIsLoading(false);
    };

    useEffect(() => {
        fetchRunningScoresCount();
    }, [selectedYear, selectedQuarter, rowsPerPage, searchValue]);

    useEffect(() => {
        fetchRunningScores();
    }, [selectedYear, selectedQuarter, rowsPerPage, page, searchValue]);

    useEffect(() => {
        setPage(0);
    }, [rowsPerPage, searchValue]);

    if (isLoading) {
        return (
            <LoadingOverlay />
        );
    }

    return (
        <>
            {runningScores.length > 0 ? (
                <>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell component="th" scope="row" align="left" sx={{ width: '15%' }}>
                                        Email Address
                                    </TableCell>
                                    {periods.map((period) => (
                                        <TableCell key={period} align="center" sx={{ width: '5%' }}>
                                            {period}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {runningScores.map((row, index) => (
                                    <StyledTableRow key={index}>
                                        <StyledTableCell component="th" scope="row">
                                            {row.email}
                                        </StyledTableCell>
                                        {periods.map((period) => (
                                            <StyledTableCell
                                                key={period}
                                                align="center"
                                                sx={{
                                                    backgroundColor:
                                                        row.scores[period] === null ? 'inherit' :
                                                            row.scores[period] <= 6 ? '#f44336' :
                                                                row.scores[period] <= 8 ? '#ffeb3b' :
                                                                    'lightgreen'
                                                }}
                                            >
                                                {row.scores[period] !== null ? row.scores[period] : '-'}
                                            </StyledTableCell>
                                        ))}
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={rowsPerPageOptions}
                        component="div"
                        count={runningScoresCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={e => setRowsPerPage(e.target.value)}
                    />
                </>
            ) : (
                <EmptyTable
                    isLoading={isLoading}
                    filtersApplied={selectedYear || selectedQuarter}
                />
            )}
        </>
    );
};

export default RunningScores;